/*
 * rn-cliq.js — vanilla-JS rebuild of the interactive widgets on the Replo-built Cliq
 * product page (Replo element 68199ccd-6a37-4f19-8d4f-ce69dac0f846, "Cliq Product",
 * product 7748611866739, handle "cliq"), for the native Shopify recreation
 * (sections/rn-cliq.liquid + snippets/rn-cliq-01..09). No Replo runtime ships on this
 * page — everything below is a from-scratch reimplementation driven off the SAME data-*
 * attributes Replo left in the extracted markup (data-replo-carousel, data-replo-
 * collapsible, data-replo-option-select, data-rn-atc, etc.), scoped to .rn-cliq only.
 *
 * VERIFIED LIVE against the source preview (network-intercepted /cart/add.js POSTs —
 * see BUILD-LOG.md §4). Key finding, ported AS-IS (not fixed, per "extract code, never
 * redraw"): the source page requires the shopper to explicitly pick a coverage-plan card
 * before Add To Cart adds the Cliq item — with nothing picked, clicking Add To Cart on the
 * LIVE Replo page only adds the "Thrive Ride" subscription upsell and silently skips the
 * Cliq product entirely (Replo's `{ref:"attributes._variant", type:"contextRef"}` action
 * resolves to nothing and is dropped, no error/prompt shown to the shopper). Confirmed
 * identical at every breakpoint (390 / 800 / 1440). A native `<select
 * data-replo-option-select-dropdown="true">` with `<option ... selected>1 Year
 * Coverage</option>` also exists in the markup but is NEVER visible/reachable at any
 * tested width (offsetParent:false everywhere) — dead legacy markup, not wired here.
 *
 * NOT reproduced here (documented, static fallback per the build brief):
 *   - The "selected" visual highlight on a coverage card: Replo drives this via its own
 *     React componentOverrides/variant system (no static CSS rule targets aria-selected —
 *     grepped the full scoped stylesheet, zero matches), so there's nothing to key off of
 *     1:1. A minimal outline/background affordance is added in initCoverageOptions() below
 *     so the picker is usable, not a pixel-match of the source's selected state.
 *   - data-replo-marquee-* -> none present on this page (Cliq has no marquee section,
 *     unlike the ring page).
 *   - data-replo-option-select-dropdown (native <select>, 4 instances) -> dead/unreachable
 *     markup at every breakpoint, confirmed live; left as inert plain <select> elements,
 *     not wired.
 */
(function () {
  "use strict";

  var ROOT_SELECTOR = ".rn-cliq";

  /* ============================================================
   * 1. COVERAGE OPTION PICKER — [data-replo-option-select="true"]
   * ============================================================
   * Each coverage tier ("1 Year Coverage" / "2 Year Coverage" / the "No, I don't want to
   * protect..." card) is its own single-option `role="listbox"` > `role="option"` mini
   * component sharing `aria-label="Cliq Coverage Plan"` (Replo's per-card selectable
   * pattern — NOT one listbox with 3 children). Grouped here by that shared aria-label
   * within the whole page (mobile/tablet and desktop each carry their own duplicate set;
   * only one set is ever visible per breakpoint, exactly like the ATC buttons below), so
   * selecting a card in whichever subtree is visible updates ONE shared module-level
   * variant id used by every Add To Cart click.
   */
  var COVERAGE_VARIANTS = [
    { match: /1\s*year/i, id: 44232080687219 }, /* "1 Year Coverage" — $69.99 */
    { match: /2\s*year/i, id: 44224409141363 }, /* "2 Year Coverage" — $75.99 */
    { match: /./, id: 44224409174131 }          /* "No, I don't want to protect..." — $59.99, catch-all */
  ];

  var selectedCliqVariantId = null; /* null until the shopper picks a card — matches the
                                        source page's verified no-default behavior */

  function variantIdForOptionText(text) {
    var t = (text || "").trim();
    for (var i = 0; i < COVERAGE_VARIANTS.length; i++) {
      if (COVERAGE_VARIANTS[i].match.test(t)) return COVERAGE_VARIANTS[i].id;
    }
    return null;
  }

  function initCoverageOptions() {
    var groups = document.querySelectorAll(ROOT_SELECTOR + ' [data-replo-option-select="true"]');
    var byLabel = {};
    groups.forEach(function (g) {
      var label = g.getAttribute("aria-label") || "default";
      (byLabel[label] = byLabel[label] || []).push(g);
    });
    Object.keys(byLabel).forEach(function (label) {
      var cards = byLabel[label];
      cards.forEach(function (card) {
        var opt = card.querySelector('[role="option"]');
        if (!opt) return;
        var variantId = variantIdForOptionText(opt.textContent);
        opt.style.cursor = "pointer";
        opt.addEventListener("click", function () {
          cards.forEach(function (c) {
            var o = c.querySelector('[role="option"]');
            if (!o) return;
            var active = o === opt;
            o.setAttribute("aria-selected", active ? "true" : "false");
            o.style.outline = active ? "2px solid currentColor" : "";
            o.style.outlineOffset = active ? "-2px" : "";
          });
          selectedCliqVariantId = variantId;
        });
        opt.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            opt.click();
          }
        });
      });
    });
  }

  /* ============================================================
   * 2. ADD TO CART — the 5 [data-rn-atc] buttons
   * ============================================================
   * Only 2 of the 5 are ever visible/reachable (confirmed live at 390 / 800 / 1440):
   *   04f7a371 -> visible on mobile + tablet ("Add To Cart")
   *   44dd3096 -> visible on desktop ("Add To Cart")
   * The other 3 (d9ea36f6, c03466b4, 4c864996) never render at any tested breakpoint —
   * dead duplicate markup, wired here too (harmless — they're simply never clickable).
   *
   * Verified live payload shapes (network-intercepted /cart/add.js POST bodies, source
   * page, both reachable buttons — see BUILD-LOG.md §4):
   *   no coverage card picked:  {"items":[{"id":"44219243855987","quantity":1,"selling_plan":3154706547}]}
   *   "No Coverage" picked:     {"items":[{"id":"44224409174131","quantity":1},{"id":"44219243855987","quantity":1,"selling_plan":3154706547}]}
   *   "1 Year Coverage" picked: {"items":[{"id":"44232080687219","quantity":1},{"id":"44219243855987","quantity":1,"selling_plan":3154706547}]}
   * The Replo runtime fires these as TWO separate addProductVariantToCart actions but they
   * land in Shopify's cart as ONE bulk /cart/add.js POST either way — reproduced here as
   * one POST with an `items` array, same net cart contents.
   */
  var THRIVE_RIDE_ITEM = { id: 44219243855987, quantity: 1, selling_plan: 3154706547 };

  function buildCartItems() {
    var items = [];
    if (selectedCliqVariantId) items.push({ id: selectedCliqVariantId, quantity: 1 });
    items.push(Object.assign({}, THRIVE_RIDE_ITEM));
    return items;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; });
      return r.json();
    });
  }

  function openCartDrawer() {
    var cd = document.querySelector("cart-drawer");
    return postJSON("/cart/update.js", {
      updates: {},
      sections: ["cart-drawer", "cart-icon-bubble"],
      sections_url: window.location.pathname
    }).then(function (data) {
      if (!cd) return data;
      if (typeof cd.renderContents === "function") {
        cd.renderContents(data);
      } else {
        cd.classList.add("animate", "active");
      }
      try {
        var freshCd = new DOMParser()
          .parseFromString(data.sections["cart-drawer"], "text/html")
          .querySelector("cart-drawer");
        if (freshCd) cd.classList.toggle("is-empty", freshCd.classList.contains("is-empty"));
      } catch (e) {}
      return data;
    });
  }

  function initAddToCart() {
    var buttons = document.querySelectorAll(ROOT_SELECTOR + " [data-rn-atc]");
    buttons.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        if (btn.getAttribute("aria-disabled") === "true") return;
        btn.setAttribute("aria-disabled", "true");
        var prevCursor = btn.style.cursor;
        btn.style.cursor = "progress";
        postJSON("/cart/add.js", { items: buildCartItems() })
          .then(function () {
            return openCartDrawer();
          })
          .catch(function (err) {
            console.error("[rn-cliq] add to cart failed", err);
          })
          .finally(function () {
            btn.removeAttribute("aria-disabled");
            btn.style.cursor = prevCursor;
          });
      });
    });
  }

  /* ============================================================
   * 3. CAROUSELS — [data-replo-carousel="true"] (5 on this page)
   * ============================================================
   * Same clone-dedup + swipe/drag engine proven on the ring page build. Replo's own
   * runtime renders extra "clone" slides (data-is-cloned="true") around the real ones for
   * a seamless infinite-scroll illusion driven by its React state machine. We don't ship
   * that state machine, so we DROP the clone duplicates (keep one DOM node per distinct
   * data-slide-source-index, preferring the data-is-target="true" one) and implement a
   * plain modulo-index prev/next + swipe carousel instead.
   */
  function initCarousels() {
    var roots = document.querySelectorAll(ROOT_SELECTOR + ' [data-replo-carousel="true"]');
    roots.forEach(function (root) {
      var track = root.querySelector('[data-replo-part="slide-track"]');
      if (!track) return;
      var prevBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
      var nextBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="next"]');
      [prevBtn, nextBtn].forEach(function (b) { if (b) { b.removeAttribute("disabled"); b.removeAttribute("aria-disabled"); } });

      var rawSlides = Array.prototype.slice.call(track.querySelectorAll('[data-replo-part="slide"]'));
      if (!rawSlides.length) return;

      var seen = {};
      var order = [];
      rawSlides.forEach(function (el) {
        var idx = el.getAttribute("data-slide-source-index") || el.getAttribute("data-slide-index") || "0";
        if (!(idx in seen)) {
          seen[idx] = el;
          order.push(idx);
        } else if (el.getAttribute("data-is-target") === "true") {
          seen[idx].parentNode && seen[idx].parentNode.removeChild(seen[idx]);
          seen[idx] = el;
        } else {
          el.parentNode && el.parentNode.removeChild(el);
        }
      });
      /* Replo's infinite-scroll clone strategy doesn't guarantee the real slide-0 node
         appears FIRST in DOM order (confirmed live: the problems-carousel's physical DOM
         order started with a clone of source-index 4, not 0) — sort numerically by
         data-slide-source-index so slide order always matches the intended sequence
         regardless of DOM/clone placement. */
      order.sort(function (a, b) { return parseInt(a, 10) - parseInt(b, 10); });
      var slides = order.map(function (idx) { return seen[idx]; });
      Array.prototype.slice.call(track.children).forEach(function (child) {
        if (slides.indexOf(child) === -1) track.removeChild(child);
      });
      /* appendChild on an already-attached node MOVES it — re-append in sorted order so
         the physical DOM order (which flexbox lays out left-to-right) actually matches
         the logical `slides` order the transform math below assumes. */
      slides.forEach(function (s) { track.appendChild(s); });

      if (slides.length < 2) {
        if (prevBtn) prevBtn.style.display = "none";
        if (nextBtn) nextBtn.style.display = "none";
        return;
      }

      /* --replo-carousel-slides-per-page is baked into the track's inline style at
         HYDRATION time by Replo's own runtime, reflecting the viewport it was captured
         at (we captured at 1440px desktop) — CSS media queries never touch this custom
         property, so at other viewports the stale baked value is wrong. Confirmed live:
         only the review-testimonial carousel (section 02) actually varies by breakpoint
         (3 slides/page above 640px, 1 at/below), every other carousel on this page is a
         constant 1 at every width — reproduced generically below rather than hardcoded to
         one carousel, since the baked ">640px" value is already correct for all of them. */
      var slidesPerPageAboveMobile = track.style.getPropertyValue("--replo-carousel-slides-per-page") || "1";
      var mobileMq = window.matchMedia("(max-width: 640px)");
      function applySlidesPerPage() {
        track.style.setProperty("--replo-carousel-slides-per-page", mobileMq.matches ? "1" : slidesPerPageAboveMobile);
      }
      applySlidesPerPage();
      /* cq2 (Leo 2026-09-03): the problems carousel (5+ slides) captured a per-page value of 1, so one card filled
         the whole track. Show as many ~300px cards as fit above mobile, like the Replo runtime does. */
      var __slideCount = track.querySelectorAll('[data-replo-part="slide"]').length;
      if (__slideCount >= 5 && !mobileMq.matches) {
        var __fit = Math.max(1, Math.floor(track.getBoundingClientRect().width / 316));
        track.style.setProperty("--replo-carousel-slides-per-page", String(__fit));
        track.style.setProperty("--replo-gap", "16px");
      }

      var current = 0;
      slides.forEach(function (s, i) {
        s.style.flexShrink = "0";
        /* Some captured slide nodes (confirmed on the problems-carousel) come from
           Replo's pooled/recycled clone set (data-slide-index="-1") and never received
           their runtime-computed width inline style at capture time — falls back to the
           node's natural (way-too-wide) content width without this. Set it explicitly
           using Replo's own formula so every slide is sized consistently regardless of
           which physical node backed it at capture time. */
        /* cq2 (Leo 2026-09-03, "this is incomplete"): the problems-carousel cards are ~300px content boxes and the
           Replo runtime shows as many as fit (4 at 1440), stepping one card at a time. When the card is narrower
           than the per-page slot, size the slide to the card so the row fills; otherwise keep Replo's formula. */
        var __card = s.firstElementChild, __cw = __card ? __card.getBoundingClientRect().width : 0, __tw = track.getBoundingClientRect().width;
        if (__cw > 0 && __cw < __tw * 0.8) { s.style.width = __cw + "px"; }
        else { s.style.width = "calc((100% + var(--replo-gap, 0px)) / var(--replo-carousel-slides-per-page, 1) - var(--replo-gap, 0px))"; }
        s.setAttribute("aria-hidden", i === 0 ? "false" : "true");
        s.setAttribute("tabindex", i === 0 ? "0" : "-1");
        s.setAttribute("data-is-active", i === 0 ? "true" : "false");
      });
      track.style.transitionProperty = "transform";
      track.style.transitionDuration = "380ms";
      track.style.transitionTimingFunction = "cubic-bezier(0.4, 0, 0.2, 1)";

      function gapPx() {
        var g = getComputedStyle(track).columnGap || getComputedStyle(track).gap || "0px";
        var n = parseFloat(g);
        return isNaN(n) ? 0 : n;
      }

      function goTo(index, opts) {
        var silent = opts && opts.silent;
        current = ((index % slides.length) + slides.length) % slides.length;
        var slideRect = slides[0].getBoundingClientRect();
        var step = slideRect.width + gapPx();
        track.style.transitionDuration = silent ? "0ms" : "380ms";
        track.style.transform = "translate3d(" + -1 * current * step + "px,0,0)";
        slides.forEach(function (s, i) {
          var active = i === current;
          s.setAttribute("aria-hidden", active ? "false" : "true");
          s.setAttribute("tabindex", active ? "0" : "-1");
          s.setAttribute("data-is-active", active ? "true" : "false");
        });
      }

      if (prevBtn) prevBtn.addEventListener("click", function () { goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener("click", function () { goTo(current + 1); });
      var dots = Array.prototype.slice.call(root.querySelectorAll('[role="button"][aria-label^="Go to slide"]'));
      if (dots.length) {
        var activeCls = (dots[0].className.split(/\s+/).filter(function (c) { return c && dots[1] && dots[1].className.split(/\s+/).indexOf(c) === -1; })[0]) || "";
        var paintDots = function () { dots.forEach(function (d, i) { var on = i === current; d.setAttribute("aria-current", on ? "true" : "false"); if (activeCls) d.classList.toggle(activeCls, on); }); };
        var _goTo = goTo; goTo = function (i, o) { _goTo(i, o); paintDots(); };
        dots.forEach(function (d, i) { d.addEventListener("click", function (e) { e.preventDefault(); goTo(i); }); });
      }

      var startX = null, deltaX = 0, dragging = false;
      track.style.touchAction = "pan-y";
      track.addEventListener("pointerdown", function (e) {
        dragging = true;
        startX = e.clientX;
        track.style.transitionDuration = "0ms";
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      });
      track.addEventListener("pointermove", function (e) {
        if (!dragging) return;
        deltaX = e.clientX - startX;
        var slideRect = slides[0].getBoundingClientRect();
        var step = slideRect.width + gapPx();
        track.style.transform = "translate3d(" + (-1 * current * step + deltaX) + "px,0,0)";
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        var slideRect = slides[0].getBoundingClientRect();
        var step = slideRect.width + gapPx();
        if (Math.abs(deltaX) > step * 0.2) {
          goTo(current + (deltaX < 0 ? 1 : -1));
        } else {
          goTo(current);
        }
        deltaX = 0;
      }
      track.addEventListener("pointerup", endDrag);
      track.addEventListener("pointercancel", endDrag);
      track.addEventListener("pointerleave", function () { if (dragging) endDrag(); });

      var resizeTimer;
      window.addEventListener("resize", function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          applySlidesPerPage();
      /* cq2 (Leo 2026-09-03): the problems carousel (5+ slides) captured a per-page value of 1, so one card filled
         the whole track. Show as many ~300px cards as fit above mobile, like the Replo runtime does. */
      var __slideCount = track.querySelectorAll('[data-replo-part="slide"]').length;
      if (__slideCount >= 5 && !mobileMq.matches) {
        var __fit = Math.max(1, Math.floor(track.getBoundingClientRect().width / 316));
        track.style.setProperty("--replo-carousel-slides-per-page", String(__fit));
        track.style.setProperty("--replo-gap", "16px");
      }
          goTo(current, { silent: true });
        }, 120);
      });

      goTo(0, { silent: true });
    });
  }

  /* ============================================================
   * 4. COLLAPSIBLES / ACCORDIONS — [data-replo-collapsible="true"]
   * ============================================================
   * Plain hidden-attribute swap, no invented smooth-height animation (matches source
   * markup exactly, same as the ring page's FAQ). Each item toggles independently.
   */
  function initCollapsibles() {
    var roots = document.querySelectorAll(ROOT_SELECTOR + ' [data-replo-collapsible="true"]');
    roots.forEach(function (root) {
      var trigger = root.querySelector('[role="button"][aria-controls]');
      if (!trigger) return;
      var panelId = trigger.getAttribute("aria-controls");
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      function toggle() {
        var isOpen = trigger.getAttribute("aria-expanded") === "true";
        var next = !isOpen;
        trigger.setAttribute("aria-expanded", next ? "true" : "false");
        root.setAttribute("data-state", next ? "open" : "closed");
        if (next) panel.removeAttribute("hidden");
        else panel.setAttribute("hidden", "");
      }
      trigger.addEventListener("click", toggle);
      trigger.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      });
    });
  }

  /* ============================================================
   * 5. ENTRANCE ANIMATIONS — Replo's "animate on scroll into view" system
   * ============================================================
   * Same generic fix as the ring build: watch every element whose
   * animation-play-state is "paused" and flip it to "running" once it's within ~15% of
   * the viewport (above-the-fold elements unpause immediately on first observe).
   */
  function initEntranceAnimations() {
    var all = document.querySelectorAll(ROOT_SELECTOR + " *");
    var candidates = [];
    all.forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.animationPlayState === "paused" && cs.animationName && cs.animationName !== "none") {
        candidates.push(el);
      }
    });
    if (!candidates.length) return;
    if (!("IntersectionObserver" in window)) {
      candidates.forEach(function (el) { el.style.animationPlayState = "running"; });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.style.animationPlayState = "running";
            io.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.01 }
    );
    candidates.forEach(function (el) { io.observe(el); });
  }

  function init() {
    initCoverageOptions();
    initAddToCart();
    initCarousels();
    initCollapsibles();
    initEntranceAnimations();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
